import type { PlmObservationInput, PlmOption } from './plm.api';
import {
  COLOURS, FRIABILITY, HOMOGENEITY, LEGACY_TREATMENTS, TREATMENTS, materialText,
  type BoolKey, type TextKey,
} from './plmStereoOptions';

/**
 * The "Stereoscopic Observation" tab — the analyst's description of the layer
 * before any fiber is identified.  Port of legacy
 * `NPPLMStereoscopicObservation_DetailView` (BTLIMS.Web/Model.xafml :33128):
 * Material Description · Homogeneity · Colour · Treatment · Friability, plus
 * the observation notes.
 *
 * Owner feedback 2026-10-07 (already in the legacy PLM form): the material is
 * ONE free-text "Material Description" box instead of a pick list, and the
 * Texture card is gone. A layer saved before that keeps what it stores — its
 * picked materials show as their names, its texture ticks round-trip untouched.
 *
 * Purely controlled — the page owns the draft and the dirty state.  In legacy
 * this tab bound to a NON-PERSISTENT twin object that was copied field-by-field
 * to and from the real record on every row change (~600 lines of `ChangeStereo`
 * overloads); in React that whole layer is just this component's props.
 */

export interface PlmStereoTabProps {
  value: PlmObservationInput;
  onChange: (next: PlmObservationInput) => void;
  /** The Materials master — only to NAME the picked materials of a layer
   *  saved before the description became free text. */
  materials: PlmOption[];
  /* sampleNotes / analysts moved OUT with the Details section: legacy keeps
     Sample Notes, Analyzed By and Analyzed Date in the HEADER block, not on
     this tab (Model.xafml :34880), so the page owns those editors now. */
  readOnly: boolean;
}

/* The option lists (COLOURS, TREATMENTS, LEGACY_TREATMENTS, HOMOGENEITY,
   FRIABILITY) and the BoolKey / TextKey types live in plmStereoOptions.ts,
   shared with the Final Confirmation tab so the two can never disagree about
   what a layer's colour / homogeneity / … is. */

export function PlmStereoTab({
  value, onChange, materials, readOnly,
}: PlmStereoTabProps) {
  const set = <K extends keyof PlmObservationInput>(key: K, v: PlmObservationInput[K]) =>
    onChange({ ...value, [key]: v });

  /** Boxes that CANNOT both be true. A sample is either uniform throughout
   *  or it is not, so legacy letting both be ticked recorded a contradiction the
   *  report then had to render (prototype v8: `excl:['Homogeneous','Heterogeneous']`).
   *  Ticking one clears the other; unticking is unaffected. */
  const EXCLUSIVE: ReadonlyArray<readonly [BoolKey, BoolKey]> = [
    ['homogeneous', 'nonHomogeneous'],
  ];

  /** Any non-prototype treatment this record actually carries (see
   *  LEGACY_TREATMENTS). Empty for a normal record, so the card then shows
   *  exactly the prototype's five. */
  const legacyTreatmentsInUse = LEGACY_TREATMENTS.filter(([k]) => value[k]);

  /** How many of a group's boxes are ticked — the prototype's right-aligned
   *  "N selected" counter (`.ogrp h4 .sel`). It lets the analyst see at a
   *  glance which groups they have touched without reading every box. */
  const selectedCount = (keys: readonly BoolKey[]) => keys.filter(k => value[k]).length;

  /** A group card's heading: UPPERCASE caption, optional required star, and
   *  the selected-count on the right (prototype v8 `.ogrp h4`). */
  const groupTitle = (label: string, opts?: { required?: boolean; count?: number }) => (
    <h4 className="plm-group__title">
      {label}
      {opts?.required && <span className="plm-req" aria-hidden="true">*</span>}
      {opts?.count ? <span className="plm-group__sel">{opts.count} selected</span> : null}
    </h4>
  );

  // Positive Stop (owner 2026-10-15, G5 parity): the layer is reported as
  // "Positive Stop" — everything but the Material Description and the box
  // itself is greyed out, and the save asks for no component.
  const stopped = value.positiveStop;
  const locked = readOnly || stopped;

  const check = (key: BoolKey, label: string) => (
    <label key={key} className="plm-chk">
      <input
        type="checkbox"
        checked={value[key]}
        disabled={readOnly || (stopped && key !== 'positiveStop')}
        onChange={e => {
          const on = e.target.checked;
          const patch: Partial<PlmObservationInput> = {
            [key]: on as PlmObservationInput[BoolKey],
          };
          if (on) {
            for (const [a, b] of EXCLUSIVE) {
              if (key === a) patch[b] = false as PlmObservationInput[BoolKey];
              if (key === b) patch[a] = false as PlmObservationInput[BoolKey];
            }
          }
          onChange({ ...value, ...patch });
        }}
      />
      <span>{label}</span>
    </label>
  );

  /** A group's free-text box is only live while that group's "Other" is ticked
   *  (prototype v8). Legacy left them always editable, so a sample could carry
   *  an "other homogeneity" description with Other unticked — text that no report
   *  ever prints and no reviewer sees. Greyed rather than hidden, so the analyst
   *  can see that ticking Other is what unlocks it. */
  const otherText = (key: TextKey, gate: BoolKey, aria: string) => (
    <input
      type="text"
      className="plm-input"
      aria-label={aria}
      value={value[key] ?? ''}
      disabled={locked || !value[gate]}
      title={value[gate] ? undefined : 'Tick "Other" to describe it'}
      onChange={e => set(key, (e.target.value || null) as PlmObservationInput[TextKey])}
    />
  );

  return (
    // Prototype v8 puts the option cards STRAIGHT into the tab panel: the tab
    // is already called "Stereoscopic Observation", so a panel captioned
    // "Observation Details" wrapped around it was a second heading for the same
    // thing — and its card border drew a box around a grid of boxes.
    <div className="plm-stereo plm-stereo-sec">

      {/* ── Material Description ───────────────────────────────────────── */}
      {/* ONE free-text box (owner feedback 2026-10-07), stored as typed. On a
          layer that still stores picked materials the box shows their names;
          left alone, the save sends the stored value back unchanged, so only an
          actual edit of the text replaces it. Two tracks wide: it is a sentence,
          not a tick list. */}
      <section className={`plm-group plm-group--material plm-group--wide${
        (value.materialIds ?? '').trim() ? '' : ' plm-group--req'}`}>
        {groupTitle('Material Description', { required: true })}
        <textarea
          className="plm-input plm-textarea"
          rows={3}
          aria-label="Material Description"
          value={materialText(value.materialIds, materials)}
          disabled={readOnly}
          onChange={e => set('materialIds', e.target.value || null)}
        />
        <div className="plm-checks">
          {check('positiveStop', 'Positive Stop')}
        </div>
        <p className="plm-hint">
          <b>Positive Stop</b>: a positive result on this layer ends analysis of
          the remaining layers of the same homogeneous area.
        </p>
        {stopped && (
          <p className="plm-ps-banner" data-testid="plm-ps-banner">
            Reported as <b>Positive Stop</b> — the other inputs of this layer are greyed out.
          </p>
        )}
      </section>

      {/* ── Homogeneity ────────────────────────────────────────────────── */}
      <section className="plm-group plm-group--homog">
        {groupTitle('Homogeneity', { count: selectedCount(HOMOGENEITY.map(([k]) => k)) })}
        <div className="plm-checks plm-checks--2">
          {HOMOGENEITY.map(([k, l]) => check(k, l))}
        </div>
        <div className="plm-field">
          {otherText('homogenityText', 'otherHomogenity', 'Homogeneity text')}
        </div>
        <p className="plm-hint">Homogeneous and Heterogeneous are mutually exclusive.</p>
      </section>

      {/* ── Colour ─────────────────────────────────────────────────────── */}
      <section className="plm-group plm-group--wide">
        {groupTitle('Color', { count: selectedCount(COLOURS.map(([k]) => k)) })}
        <div className="plm-checks plm-checks--5">
          {COLOURS.map(([k, l]) => check(k, l))}
        </div>
        <div className="plm-field">
          {/* Colour has no "Other" box — its catch-all is "Various Colors", so
              that is what unlocks the description. Flagged for the lab: if they
              expect the colour text free at all times, this is the one line to
              change. */}
          {otherText('colorText', 'variousColors', 'Color text')}
        </div>
      </section>

      {/* ── Sample treatment ───────────────────────────────────────────── */}
      <section className="plm-group plm-group--treatment">
        {groupTitle('Treatment', {
          count: selectedCount([...TREATMENTS, ...legacyTreatmentsInUse].map(([k]) => k)),
        })}
        <div className="plm-checks plm-checks--3">
          {TREATMENTS.map(([k, l]) => check(k, l))}
          {legacyTreatmentsInUse.map(([k, l]) => check(k, l))}
        </div>
      </section>

      {/* ── Stereoscopic ───────────────────────────────────────────────── */}
      {/* Stereoscopic + Friability share ONE grid cell, stacked. As separate
          cells Friability sat in grid ROW 2, which begins only after row 1
          ends - and row 1 is as tall as Colour, so it landed far below the
          card it belongs under (owner report 2026-09-24). */}
      <section className="plm-group plm-group--stereo">
        {groupTitle('Stereoscopic')}
        <div className="plm-checks">
          {check('fibersPresent', 'Fibers Present')}
        </div>
      </section>

      {/* ── Friability ───────────────────────────────────────────────────── */}
      <section className={`plm-group plm-group--friability${
        value.friability == null && !stopped ? ' plm-group--req' : ''}`}>
        {groupTitle('Friability', { required: true })}
        <div className="plm-checks plm-checks--3">
          {FRIABILITY.map(([v, label]) => (
            <label key={v} className="plm-chk">
              <input
                type="radio"
                name="plm-friability"
                checked={value.friability === v}
                disabled={locked}
                onChange={() => set('friability', v)}
              />
              <span>{label}</span>
            </label>
          ))}
        </div>
      </section>

      {/* ── Asbestos Stereomicroscopic % ───────────────────────────────────
          Legacy's LAST column (Model.xafml :33251 — LayoutGroup
          Id="Fiber Observation", Index 7), holding exactly two items:
            FiberObservation    the % box, ShowCaption="False" (70×40)
            NoAsbestosDetected  the checkbox
          AI had the % box mislabelled "Fiber Observation" inside Material and
          the checkbox inside Stereoscopic — both belong here. */}
      <section className="plm-group plm-group--asbestos">
        {groupTitle('Asbestos Stereomicroscopic %')}
        {/* No caption, as in legacy — the group heading names the field.
            Ticking "No Asbestos Detected" CLEARS and LOCKS the % (prototype v8):
            legacy allowed both, so a layer could read "no asbestos detected" and
            carry a percentage, which the report then had to print side by side.
            Unticking releases the box; the value is not restored, because the
            analyst has just declared there was none. */}
        <div className="plm-field plm-field--nolabel">
          <input
            type="text"
            className="plm-input"
            aria-label="Asbestos Stereomicroscopic %"
            value={value.noAsbestosDetected ? '' : (value.fiberObservation ?? '')}
            disabled={locked || value.noAsbestosDetected}
            title={value.noAsbestosDetected
              ? 'Cleared because No Asbestos Detected is ticked'
              : undefined}
            onChange={e => set('fiberObservation',
              (e.target.value || null) as PlmObservationInput['fiberObservation'])}
          />
        </div>
        <div className="plm-checks">
          <label className="plm-chk">
            <input
              type="checkbox"
              checked={value.noAsbestosDetected}
              disabled={locked}
              onChange={e => {
                const on = e.target.checked;
                onChange({
                  ...value,
                  noAsbestosDetected: on,
                  // Clear on the SAME change, so the saved row can never hold
                  // both. Doing it in an effect would leave a frame where it did.
                  ...(on ? { fiberObservation: null } : {}),
                });
              }}
            />
            <span>No Asbestos Detected</span>
          </label>
        </div>
        <p className="plm-hint">Ticking NAD clears and locks the %.</p>
      </section>

    </div>
  );
}
